import { useEffect, useState } from "react";

import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { ClinicianSearchStrategy, PersonSearchAutocompleteCustom } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { CliniciansHelper } from "@/Common/Helpers/CliniciansHelper";
import { Person } from "@/Common/Models/Person/Person";

interface PopulationBasedFundingTransferPatientRegisterPopupProps {
    onClose: () => void;
    onTrySubmit: (sourcePhysicianId: string, targetPhysicianId: string) => Promise<boolean>;
}

function PopulationBasedFundingTransferPatientRegisterPopup(props: PopulationBasedFundingTransferPatientRegisterPopupProps): JSX.Element {
    const { getText } = useTranslation();

    const [selectedSourcePhysician, setSelectedSourcePhysician] = useState<[Person]>();
    const [selectedTargetPhysician, setSelectedTargetPhysician] = useState<[Person]>();
    const [warningPopupVisible, setWarningPopupIsVisible] = useState<boolean>(false);

    const [isSaving, setIsSaving] = useState<boolean>(false);

    const clinicianSearchStrategy: ClinicianSearchStrategy = CliniciansHelper.GetAllCliniciansSearchStrategy();

    const selectedSourceAndTargetPhysiciansAreTheSameOnes = selectedSourcePhysician?.any()
        && selectedTargetPhysician?.any()
        && selectedSourcePhysician?.[0]?.ContactFacetId == selectedTargetPhysician?.[0]?.ContactFacetId;

    const isFormValid = !isSaving
        && !!selectedSourcePhysician
        && !!selectedTargetPhysician
        && !selectedSourceAndTargetPhysiciansAreTheSameOnes;

    useEffect(() => {
        const onSave = async (): Promise<void> => {
            if (await props.onTrySubmit(selectedSourcePhysician[0]?.ContactFacetId, selectedTargetPhysician[0]?.ContactFacetId)) {
                props.onClose();
            }
            else {
                setIsSaving(false);
            }
        };

        if (isSaving) {
            onSave();
        }
    }, [isSaving]);

    const renderRow = (label: string, element: JSX.Element, col = 10): JSX.Element => (
        <div className="row">
            <div className={"col-md-" + col}>
                {label && (<label className="bold">{label}</label>)}
                {element}
            </div>
        </div>
    );

    const renderTransferPopup = (): JSX.Element => {
        const body = (
            <div className="flex-column flex-gap-md margin-md-top margin-md-bottom">
                {renderRow(
                    null,
                    <label>{getText("TransferPatientRegisterExplanationLabel")}</label>
                )}
                {renderRow(
                    getText("PhysicianLabel"),
                    <PersonSearchAutocompleteCustom
                        componentId="source_physician_list_search_input"
                        focusOnLoad={true}
                        allowMultipleItemSelection={false}
                        closeDropDownAfterSelection
                        selectedItemList={selectedSourcePhysician?.toImmutable()}
                        valid={!!selectedSourcePhysician && !selectedSourceAndTargetPhysiciansAreTheSameOnes}
                        searchStrategy={clinicianSearchStrategy}
                        onSelectionCallback={item => setSelectedSourcePhysician([item.Item])}
                        onClearCallback={() => setSelectedSourcePhysician(null)}
                    />
                )}

                <label>{getText("ToLabel")}:</label>

                {renderRow(
                    getText("PhysicianLabel"),
                    <PersonSearchAutocompleteCustom
                        componentId="target_physician_list_search_input"
                        focusOnLoad={false}
                        allowMultipleItemSelection={false}
                        closeDropDownAfterSelection
                        selectedItemList={selectedTargetPhysician?.toImmutable()}
                        valid={!!selectedTargetPhysician && !selectedSourceAndTargetPhysiciansAreTheSameOnes}
                        searchStrategy={clinicianSearchStrategy}
                        onSelectionCallback={item => setSelectedTargetPhysician([item.Item])}
                        onClearCallback={() => setSelectedTargetPhysician(null)}
                    />
                )}

                {selectedSourceAndTargetPhysiciansAreTheSameOnes && (<label className="invalid">{getText("SelectedPhysiciansShouldBeDifferentOnesWarningLabel")}</label>)}
            </div>
        );

        const footer = (
            <div className="p-footer">
                <div className="footer-button-container">
                    <ButtonControl
                        className="btn btn-default"
                        onClick={() => props.onClose()}
                        label={getText("CancelLabel")}
                    />
                    <ButtonControl
                        id="save_registration_butt"
                        className="btn btn-primary"
                        disabled={!isFormValid}
                        label={getText("TransferLabel")}
                        onClick={() => setWarningPopupIsVisible(true)}
                    />
                </div>
            </div>
        );

        return (
            <Modal
                id="PopulationBasedFundingTransferPatientRegisterPopup"
                title={getText("TransferPatientRegisterLabel")}
                onClose={() => props.onClose()}
                top={50}
                width={480}
                draggable
                showOverlay={true}
                footer={footer}
                showX={true}
                roundTopCorner={true}
                disableOverlayClose={true}
            >
                {body}
            </Modal>
        );
    };

    const renderWarningPopup = (): JSX.Element => {
        const footer = (
            <div className="p-footer">
                <div className="footer-button-container">
                    <ButtonControl
                        className="btn btn-default"
                        onClick={() => setWarningPopupIsVisible(false)}
                        label={getText("CancelLabel")}
                    />
                    <ButtonControl
                        id="save_registration_butt"
                        className="btn btn-primary"
                        label={getText("OKLabel")}
                        onClick={() => setIsSaving(true)}
                    />
                </div>
            </div>
        );

        return (
            <Modal
                id="PopulationBasedFundingTransferPatientRegisterWarningPopup"
                title={cacheStore.GetString("WarningLabel")}
                onClose={() => props.onClose()}
                footer={footer}
                top={180}
                width={395}
                draggable
                showOverlay={true}
            >
                {getText("TransferedPatientWarning")}
            </Modal>
        );
    };

    return (
        <>
            {!warningPopupVisible && (renderTransferPopup())}
            {warningPopupVisible && (renderWarningPopup())}
        </>
    );
}

export { PopulationBasedFundingTransferPatientRegisterPopup };
